/**
 * Where the thread panel sits next to the channel, and how wide it is.
 *
 * Both are per-device viewport preferences rather than account state, so they
 * live in `localStorage` and are read back at store creation.
 */

/**
 * `pinned` docks the thread as a column that shares the shell's horizontal
 * space with the channel, which reflows to whatever is left. `float` lifts the
 * thread out of that flow and onto the channel's right edge instead: the
 * channel keeps its own width, and pays for it by having that edge covered —
 * the members panel, the header's Members toggle, the composer's right-hand
 * controls and the right-aligned message actions are all behind the thread
 * until it is closed or pinned.
 *
 * Floating is the default because opening a thread is the common, short-lived
 * act, and it should not rearrange the channel the member is reading. The ways
 * back out match that: the close control and the placement toggle sit in the
 * thread's own header, and because floating is the placement that covers
 * something, a press on the covered channel puts the thread away by itself.
 */
export type ThreadPanelMode = 'float' | 'pinned';

export const DEFAULT_THREAD_PANEL_MODE: ThreadPanelMode = 'float';

/**
 * The widths the thread may be set to. The maximum is what a member reading a
 * long reply on a wide monitor can ask for, and it is deliberately wider than
 * either placement can draw on a small window: what a given window has room for
 * is the shell's ceiling to impose, and it caps the drawn column without
 * touching the stored width, which returns when the window does.
 */
export const MIN_THREAD_PANEL_WIDTH = 280;
export const MAX_THREAD_PANEL_WIDTH = 1000;
export const DEFAULT_THREAD_PANEL_WIDTH = 380;

const MODE_KEY = 'borgee:thread:panelMode';
const WIDTH_KEY = 'borgee:thread:panelWidth';

/** One width serves both placements, so the same bounds hold in both. */
export const clampThreadPanelWidth = (width: number): number =>
  Math.min(MAX_THREAD_PANEL_WIDTH, Math.max(MIN_THREAD_PANEL_WIDTH, Math.round(width)));

// `localStorage` throws instead of returning null under private browsing and
// when the origin is out of quota, and its contents are whatever any past or
// tampered-with version of the app left behind. A stored value that is not a
// mode this build knows, or not a usable number, is therefore discarded in
// favour of the default rather than trusted into the layout.
export const loadThreadPanelMode = (): ThreadPanelMode => {
  try {
    const stored = window.localStorage.getItem(MODE_KEY);
    return stored === 'float' || stored === 'pinned' ? stored : DEFAULT_THREAD_PANEL_MODE;
  } catch {
    return DEFAULT_THREAD_PANEL_MODE;
  }
};

export const saveThreadPanelMode = (mode: ThreadPanelMode): void => {
  try {
    window.localStorage.setItem(MODE_KEY, mode);
  } catch {
    // Losing the preference only costs the member one click next session.
  }
};

export const loadThreadPanelWidth = (): number => {
  try {
    const stored = window.localStorage.getItem(WIDTH_KEY);
    if (stored === null) return DEFAULT_THREAD_PANEL_WIDTH;
    const parsed = Number(stored);
    return Number.isFinite(parsed) && parsed > 0
      ? clampThreadPanelWidth(parsed)
      : DEFAULT_THREAD_PANEL_WIDTH;
  } catch {
    return DEFAULT_THREAD_PANEL_WIDTH;
  }
};

export const saveThreadPanelWidth = (width: number): void => {
  try {
    window.localStorage.setItem(WIDTH_KEY, String(width));
  } catch {
    // Same as the mode: a width that fails to persist is not worth a failure.
  }
};
